import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPrinter, FiEdit, FiTrash2, FiFileText, FiRefreshCw, FiX } from 'react-icons/fi';
import { database } from '../../Firebase';
import { ref, push, onValue, update, remove } from 'firebase/database';
import Namuna7Receipt from './Namuna7Receipt';
import Namuna17Report from './Namuna17Report';
import Namuna12Receipt from './Namuna12Receipt';

const DepositManagement = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { grampanchayatName, arthikVarshLabel } = location.state || {};

    const [view, setView] = useState('list'); // 'list', 'new', 'edit'
    const [searchTerm, setSearchTerm] = useState('');
    const [deposits, setDeposits] = useState([]);
    const [persons, setPersons] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    
    // Print State
    const [printMode, setPrintMode] = useState(null); // 'namuna7', etc.
    const [selectedDeposit, setSelectedDeposit] = useState(null);
    
    // Form State
    const [formData, setFormData] = useState({
        depositNo: '',
        date: new Date().toISOString().split('T')[0],
        personName: '',
        personId: '',
        depositAmount: '',
        openingBalance: '0',
        accountNo: '',
        depositType: 'Fixed Deposit', // Default
        narration: '',
        // Transaction Details
        receiptNo: '',
        ledgerId: '',
        ledgerName: '',
        subLedger: '',
        accountBalance: '0'
    });

    const [isPersonSearchOpen, setIsPersonSearchOpen] = useState(false);
    const [personFilter, setPersonFilter] = useState('');

    // New Person Modal State
    const [isNewPersonModalOpen, setIsNewPersonModalOpen] = useState(false);
    const [newPerson, setNewPerson] = useState({
        title: 'Mr.',
        firstName: '',
        middleName: '',
        lastName: '',
        mobileNo: '',
        aadharNo: '',
        villageName: 'इनाम धामणी'
    });

    const handleSaveNewPerson = () => {
        if(!newPerson.firstName) {
            alert('Please enter at least First Name');
            return;
        }

        const fullName = `${newPerson.firstName} ${newPerson.middleName} ${newPerson.lastName}`.trim();
        const personData = {
            ...newPerson,
            fullName,
            createdAt: new Date().toISOString()
        };

        const newPersonRef = push(ref(database, 'Grampanchayat/Gramniddhi/Certificates and licenses/PersonList'));
        
        update(newPersonRef, personData)
            .then(() => {
                alert('Person Added Successfully!');
                setIsNewPersonModalOpen(false);
                setNewPerson({
                    title: 'Mr.',
                    firstName: '',
                    middleName: '',
                    lastName: '',
                    mobileNo: '',
                    aadharNo: '',
                    villageName: 'इनाम धामणी'
                });
                // Auto Select the new person
                setFormData(prev => ({
                    ...prev,
                    personName: fullName,
                    personId: newPersonRef.key
                }));
                setIsPersonSearchOpen(false);
            })
            .catch(err => alert('Error saving person: ' + err.message));
    };

    // Fetch Data
    useEffect(() => {
        // Fetch Deposits
        const depositsRef = ref(database, 'Grampanchayat/Panipuravtha/Transactions/Deposits');
        onValue(depositsRef, (snapshot) => {
            const data = [];
            snapshot.forEach((child) => {
                data.push({ id: child.key, ...child.val() });
            });
            setDeposits(data);
            // Auto-generate next Deposit No
            if (view === 'new') {
                const nextNo = data.length > 0 ? Math.max(...data.map(d => parseInt(d.depositNo) || 0)) + 1 : 1;
                setFormData(prev => ({ ...prev, depositNo: nextNo.toString() }));
            }
        });

        // Fetch Persons
        const personRef = ref(database, 'Grampanchayat/Gramniddhi/Certificates and licenses/PersonList');
        onValue(personRef, (snapshot) => {
            const data = [];
            snapshot.forEach((child) => {
                data.push({ id: child.key, ...child.val() });
            });
            setPersons(data);
        });

        // Fetch Ledgers (Account Groups/Ledgers for dropdown)
         const ledgerRef = ref(database, 'Grampanchayat/Panipuravtha/Masters/AccountGroups');
         onValue(ledgerRef, (snapshot) => {
             const data = [];
             snapshot.forEach(child => {
                 data.push({ id: child.key, ...child.val() });
             });
             setLedgers(data);
         });

    }, [view]);

    // Return State
    const [returns, setReturns] = useState([]);
    const [selectedDepositForReturn, setSelectedDepositForReturn] = useState(null);
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [newReturn, setNewReturn] = useState({
        date: new Date().toISOString().split('T')[0],
        returnAmount: '',
        paymentMode: 'cash',
        voucherNo: '',
        voucherDate: new Date().toISOString().split('T')[0],
        resolutionNo: '',
        resolutionDate: '',
        ledgerId: '',
        accountId: '',
        accountBalance: '11000', // Dummy for now
        returnNarration: ''
    });

    // Fetch Returns when separate deposit selected
    useEffect(() => {
        if (!selectedDepositForReturn) return;
        
        const returnsRef = ref(database, 'Grampanchayat/Panipuravtha/Transactions/DepositReturns');
        const unsubscribe = onValue(returnsRef, (snapshot) => {
            const data = snapshot.val();
            const loadedReturns = [];
            for (const key in data) {
                if (data[key].depositId === selectedDepositForReturn.id) {
                    loadedReturns.push({ id: key, ...data[key] });
                }
            }
            setReturns(loadedReturns);
        });
        
        return () => unsubscribe();
    }, [selectedDepositForReturn]);

    const handleSaveReturn = () => {
         if (!newReturn.returnAmount || !newReturn.date) {
            alert("महत्वाची माहिती भरा (रक्कम, दिनांक)");
            return;
        }

        const returnData = {
            ...newReturn,
            depositId: selectedDepositForReturn.id,
            createdAt: new Date().toISOString()
        };

        push(ref(database, 'Grampanchayat/Panipuravtha/Transactions/DepositReturns'), returnData)
            .then(() => {
                alert("अनामत परत साठवली!");
                setView('returnList');
                setNewReturn({
                    date: new Date().toISOString().split('T')[0],
                    returnAmount: '',
                    paymentMode: 'cash',
                    voucherNo: '',
                    voucherDate: new Date().toISOString().split('T')[0],
                    resolutionNo: '',
                    resolutionDate: '',
                    ledgerId: '',
                    accountId: '',
                    accountBalance: '11000',
                    returnNarration: ''
                });
            })
            .catch(err => alert("Error saving return: " + err.message));
    };

    const handleSave = () => {
        if (!formData.personName || !formData.depositAmount) {
            alert('Please fill required fields (Person Name, Amount)');
            return;
        }

        const newDeposit = {
            ...formData,
            createdAt: new Date().toISOString()
        };

        push(ref(database, 'Grampanchayat/Panipuravtha/Transactions/Deposits'), newDeposit)
            .then(() => {
                alert('Deposit Saved Successfully!');
                setView('list');
                setFormData({
                    depositNo: '',
                    date: new Date().toISOString().split('T')[0],
                    personName: '',
                    personId: '',
                    depositAmount: '',
                    openingBalance: '0',
                    accountNo: '',
                    depositType: 'Fixed Deposit',
                    narration: '',
                    receiptNo: '',
                    ledgerId: '',
                    ledgerName: '',
                    subLedger: '',
                    accountBalance: '0'
                });
            })
            .catch(err => alert('Error: ' + err.message));
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this deposit?')) {
            remove(ref(database, `Grampanchayat/Panipuravtha/Transactions/Deposits/${id}`))
                .then(() => alert('Deleted Successfully'))
                .catch(err => alert('Error: ' + err.message));
        }
    };

    // Filter Logic
    const filteredDeposits = deposits.filter(d => 
        (d.personName && d.personName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (d.depositNo && d.depositNo.includes(searchTerm))
    );

    const filteredPersons = persons.filter(p => 
        p.fullName && p.fullName.toLowerCase().includes(personFilter.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 font-poppins text-sm">
             {/* Header */}
             <div className="bg-[#267c77] text-white p-4 flex justify-between items-center shadow-md">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => {
                            if (view === 'list') navigate(-1);
                            else setView('list');
                        }}
                        className="hover:bg-blue-700 p-2 rounded-full transition-colors"
                    >
                        <FiArrowLeft className="text-xl" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold">
                            {view === 'list' ? 'अनामत माहिती' : 'नवीन अनामत नोंदणी'}
                        </h1>
                        <p className="text-xs text-[#267c77]/30">
                             {grampanchayatName ? `${grampanchayatName} | ` : ''}आर्थिक वर्ष: {arthikVarshLabel || '२०२४-२५'}
                        </p>
                    </div>
                </div>
                 <div className="flex gap-2">
                    {view === 'list' && (
                        <button 
                            onClick={() => setView('new')} 
                            className="bg-[#1e6b66] hover:bg-[#155a55] text-white px-4 py-2 rounded font-bold shadow"
                        >
                            नवीन
                        </button>
                    )}
                 </div>
            </div>

            <div className="p-4 max-w-7xl mx-auto">
                {view === 'list' && (
                    <div className="bg-white rounded-lg shadow border border-[#267c77]/30 overflow-hidden">
                        {/* Search */}
                         <div className="flex p-4 gap-2 border-b border-gray-100 bg-[#267c77]/10">
                            <input 
                                type="text" 
                                placeholder="शोधणे" 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border border-[#267c77]/30 p-2 rounded w-full md:w-1/2 focus:outline-none focus:ring-2 focus:ring-[#267c77]"
                            />
                            <button className="bg-[#267c77] text-white px-6 py-2 rounded font-medium hover:bg-[#1e6b66]">
                                शोधणे
                            </button>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-[#267c77] text-white text-sm">
                                    <tr>
                                        <th className="p-3 border border-[#1e6b66] text-center">अनामत नं.</th>
                                        <th className="p-3 border border-[#1e6b66]">व्यक्तीचे नाव</th>
                                        <th className="p-3 border border-[#1e6b66] text-center">दिनांक</th>
                                        <th className="p-3 border border-[#1e6b66] text-right">अनामत रक्कम</th>
                                        <th className="p-3 border border-[#1e6b66] text-center">खतावाणी क्र.</th>
                                        <th className="p-3 border border-[#1e6b66] text-center">खाते क्र.</th>
                                        <th className="p-3 border border-[#1e6b66] text-center">पर्याय</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDeposits.length > 0 ? (
                                        filteredDeposits.map((dep, idx) => (
                                            <tr key={dep.id} className="hover:bg-[#267c77]/10 border-b border-[#267c77]/30">
                                                <td className="p-3 border border-[#267c77]/30 text-center">{dep.depositNo}</td>
                                                <td className="p-3 border border-[#267c77]/30 font-medium">{dep.personName}</td>
                                                <td className="p-3 border border-[#267c77]/30 text-center">{new Date(dep.date).toLocaleDateString('en-GB')}</td>
                                                <td className="p-3 border border-[#267c77]/30 text-right">{dep.depositAmount}</td>
                                                <td className="p-3 border border-[#267c77]/30 text-center">{dep.ledgerId || '-'}</td>
                                                <td className="p-3 border border-[#267c77]/30 text-center">{dep.accountNo || '-'}</td>
                                                <td className="p-3 border border-[#267c77]/30 text-center">
                                                    <div className="flex justify-center gap-2">
                                                        <button 
                                                            onClick={() => handleDelete(dep.id)}
                                                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs"
                                                        >
                                                            खोडणे
                                                        </button>
                                                        <button 
                                                            onClick={() => {
                                                                const receiptData = {
                                                                    ...dep,
                                                                    challanNo: dep.receiptNo || '-',
                                                                    personName: dep.personName,
                                                                    customContent: 'कडून अनामत रक्कमा च्या खाती रक्कम रुपये',
                                                                    totalAmount: parseFloat(dep.depositAmount || 0)
                                                                };
                                                                setSelectedDeposit(receiptData);
                                                                setPrintMode('namuna7');
                                                            }}
                                                            className="bg-[#267c77] hover:bg-[#1e6b66] text-white px-3 py-1 rounded text-xs"
                                                        >
                                                            नमुना नं. 7
                                                        </button>
                                                        <button 
                                                            onClick={() => {
                                                                setSelectedDeposit(dep);
                                                                setPrintMode('namuna17');
                                                            }}
                                                            className="bg-[#1e6b66] hover:bg-[#155a55] text-white px-3 py-1 rounded text-xs"
                                                        >
                                                            रिपोर्ट
                                                        </button>
                                                        <button 
                                                            onClick={() => {
                                                                setSelectedDepositForReturn(dep);
                                                                setView('returnList');
                                                            }}
                                                            className="bg-[#267c77] hover:bg-[#1e6b66] text-white px-3 py-1 rounded text-xs"
                                                        >
                                                            अनामत परत
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="p-8 text-center text-gray-500">
                                                No Deposits Found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {view === 'new' && (
                    <div className="max-w-5xl mx-auto space-y-6">
                        {/* Section 1: Anamat Mahiti */}
                        <div className="bg-white rounded border border-[#267c77]/30 overflow-hidden shadow-lg">
                            <div className="bg-[#267c77] text-white px-4 py-2 font-semibold">
                                अनामत माहिती
                            </div>
                            <div className="p-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">अनामत नं.:</label>
                                        <input 
                                            value={formData.depositNo}
                                            onChange={(e) => setFormData({...formData, depositNo: e.target.value})}
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-[#267c77]"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">दिनांक:</label>
                                        <input 
                                            type="date"
                                            value={formData.date}
                                            onChange={(e) => setFormData({...formData, date: e.target.value})}
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-[#267c77]"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <label className="w-32 text-gray-700 font-medium">व्यक्तीचे नाव :</label>
                                    <div className="flex-1 flex gap-2">
                                        <input 
                                            value={formData.personName}
                                            readOnly
                                            placeholder="व्यक्तीचे नाव निवडा"
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 bg-gray-50"
                                        />
                                        <button 
                                            onClick={() => setIsPersonSearchOpen(true)}
                                            className="bg-[#267c77] text-white px-4 py-2 rounded hover:bg-[#1e6b66]"
                                        >
                                            ...
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">अनामत रक्कम:</label>
                                        <input 
                                            type="number"
                                            value={formData.depositAmount}
                                            onChange={(e) => setFormData({...formData, depositAmount: e.target.value})}
                                            placeholder="अनामत रक्कम भरा."
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-[#267c77]"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">आरंभीची शिल्लक:</label>
                                        <input 
                                            type="number"
                                            value={formData.openingBalance}
                                            onChange={(e) => setFormData({...formData, openingBalance: e.target.value})}
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-[#267c77] bg-gray-50"
                                            readOnly // Usually 0 for new
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">खाते नं.:</label>
                                        <input 
                                            value={formData.accountNo}
                                            onChange={(e) => setFormData({...formData, accountNo: e.target.value})}
                                            placeholder="Auto Generated"
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-[#267c77] bg-gray-50"
                                            readOnly
                                        />
                                    </div>
                                     <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">अनामत प्रकार:</label>
                                        <select 
                                            value={formData.depositType}
                                            onChange={(e) => setFormData({...formData, depositType: e.target.value})}
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-[#267c77] bg-white"
                                        >
                                            <option>अधिकृत ठेव</option>
                                            <option>इतर ठेव</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <label className="w-32 text-gray-700 font-medium pt-2">शेरा:</label>
                                    <textarea 
                                        value={formData.narration}
                                        onChange={(e) => setFormData({...formData, narration: e.target.value})}
                                        placeholder="शेरा भरा."
                                        rows="3"
                                        className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-[#267c77]"
                                    />
                                </div>

                                <div className="flex items-center gap-6 pl-32">
                                     <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" name="paymentType" defaultChecked className="w-4 h-4 text-[#267c77]" />
                                        <span>रोख</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" name="paymentType" className="w-4 h-4 text-[#267c77]" />
                                        <span>वर्ग / ट्रान्स्फर</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                         {/* Section 2: Transaction Details (Vyavahar Tapshil) */}
                         <div className="bg-white rounded border border-blue-200 overflow-hidden shadow-lg">
                            <div className="bg-[#267c77] text-white px-4 py-2 font-semibold">
                                व्यवहार तपशील
                            </div>
                            <div className="p-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">पावती नं. :</label>
                                        <input 
                                            value={formData.receiptNo}
                                            onChange={(e) => setFormData({...formData, receiptNo: e.target.value})}
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-[#267c77]"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">दिनांक :</label>
                                        <input 
                                            type="date"
                                            value={formData.date}
                                            readOnly
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 bg-gray-50"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <label className="w-32 text-gray-700 font-medium">व्यक्तीचे नाव :</label>
                                    <input 
                                        value={formData.personName}
                                        readOnly
                                        className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 bg-gray-50"
                                    />
                                </div>

                                {/* Ledger / Sub Ledger Row */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                     <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">खतावणी :</label>
                                        <div className="flex gap-1 flex-1">
                                            <input 
                                                className="border border-[#267c77]/30 rounded px-2 py-2 w-16" 
                                                value={formData.ledgerId}
                                                onChange={(e) => setFormData({...formData, ledgerId: e.target.value})}
                                                placeholder="Code"
                                            />
                                            <button className="bg-[#267c77] text-white px-3 rounded">...</button>
                                            <input 
                                                className="border border-[#267c77]/30 rounded px-2 py-2 flex-1 bg-gray-50" 
                                                placeholder="Ledger Name" 
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">वाटणीची रक्कम :</label>
                                        <input 
                                            value="0"
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 bg-gray-50"
                                            readOnly
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                     <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">खाते :</label>
                                        <div className="flex gap-1 flex-1">
                                            <input 
                                                className="border border-[#267c77]/30 rounded px-2 py-2 w-16"
                                                placeholder="0" 
                                            />
                                            <button className="bg-[#267c77] text-white px-3 rounded">...</button>
                                            <input 
                                                className="border border-[#267c77]/30 rounded px-2 py-2 flex-1" 
                                                placeholder="नवीन खाते" 
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">खाते शिल्लक :</label>
                                        <input 
                                            value={formData.depositAmount} 
                                            className="border border-[#267c77]/30 rounded px-3 py-2 flex-1 bg-gray-50"
                                            readOnly
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <label className="w-32 text-gray-700 font-medium">व्यवहार रक्कम :</label>
                                    <input 
                                        value={formData.depositAmount}
                                        readOnly
                                        className="border border-[#267c77]/30 rounded px-3 py-2 w-64 bg-gray-50"
                                    />
                                </div>
                            </div>
                        </div>

                         <div className="flex justify-center gap-4 py-4">
                            <button 
                                onClick={handleSave}
                                className="bg-[#1e6b66] text-white px-8 py-2 rounded-lg font-bold text-lg hover:bg-[#155a55] shadow-md"
                            >
                                साठविणे
                            </button>
                            <button 
                                onClick={() => setView('list')}
                                className="bg-red-500 text-white px-8 py-2 rounded-lg font-bold text-lg hover:bg-red-600 shadow-md"
                            >
                                रद्द करणे
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Person Select Modal */}
            {isPersonSearchOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] font-poppins text-sm">
                    <div className="bg-white rounded-lg w-full max-w-5xl h-[85vh] flex flex-col shadow-2xl overflow-hidden">
                        {/* Header */}
                        <div className="bg-[#267c77] text-white px-4 py-3 flex justify-between items-center">
                            <h3 className="text-lg font-bold">व्यक्तीची माहिती</h3>
                            <button onClick={() => setIsPersonSearchOpen(false)} className="hover:bg-[#1e6b66] p-1 rounded">
                                <FiX size={20} />
                            </button>
                        </div>

                        {/* Search Fields & New Button */}
                        <div className="p-4 bg-gray-50 border-b border-gray-200">
                             <div className="flex flex-wrap gap-4 items-end">
                                <div className="flex-1 min-w-[150px]">
                                    <label className="block text-gray-700 font-medium mb-1">आधार क्र.</label>
                                    <input 
                                        placeholder="आधार नं." 
                                        className="border border-[#267c77]/30 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#267c77]"
                                    />
                                </div>
                                <div className="flex-1 min-w-[150px]">
                                    <label className="block text-gray-700 font-medium mb-1">नाव</label>
                                    <input 
                                        placeholder="पहिले नाव" 
                                        value={personFilter}
                                        onChange={(e) => setPersonFilter(e.target.value)}
                                        className="border border-[#267c77]/30 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#267c77]"
                                    />
                                </div>
                                <div className="flex-1 min-w-[150px]">
                                    <label className="block text-gray-700 font-medium mb-1">वडिलांचे/पतीचे नाव</label>
                                    <input 
                                        placeholder="वडिलांचे/पतीचे नाव" 
                                        className="border border-[#267c77]/30 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#267c77]"
                                    />
                                </div>
                                <div className="flex-1 min-w-[150px]">
                                    <label className="block text-gray-700 font-medium mb-1">आडनाव</label>
                                    <input 
                                        placeholder="आडनाव" 
                                        className="border border-[#267c77]/30 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#267c77]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-1">पर्याय</label>
                                    <button 
                                        className="bg-[#267c77] text-white px-6 py-2 rounded font-bold hover:bg-[#1e6b66] shadow-md"
                                        onClick={() => setIsNewPersonModalOpen(true)}
                                    >
                                        नविन
                                    </button>
                                </div>
                             </div>
                        </div>

                        {/* Table Header */}
                        <div className="bg-[#267c77] text-white grid grid-cols-5 text-center py-2 font-semibold">
                             <div className="border-r border-[#1e6b66]">व्यक्ती नं.</div>
                             <div className="border-r border-[#1e6b66]">नाव</div>
                             <div className="border-r border-[#1e6b66]">वडिल/पतीचे नाव</div>
                             <div className="border-r border-[#1e6b66]">आडनाव</div>
                             <div>आधार नं.</div>
                        </div>

                         {/* Results Table */}
                         <div className="flex-1 overflow-y-auto">
                             {filteredPersons.length > 0 ? (
                                 filteredPersons.map((p, idx) => (
                                     <div 
                                        key={p.id} 
                                        onClick={() => {
                                            setFormData(prev => ({
                                                ...prev, 
                                                personName: p.fullName,
                                                personId: p.id
                                            }));
                                            setIsPersonSearchOpen(false);
                                        }}
                                        className="grid grid-cols-5 text-center py-2 border-b hover:bg-[#267c77]/10 cursor-pointer transition-colors"
                                     >
                                         <div className="border-r border-gray-100">{idx + 1}</div>
                                         <div className="border-r border-gray-100 font-medium text-left px-2">{p.firstName || p.fullName?.split(' ')[0]}</div>
                                         <div className="border-r border-gray-100 text-left px-2">{p.middleName || p.fullName?.split(' ')[1] || '-'}</div>
                                         <div className="border-r border-gray-100 text-left px-2">{p.lastName || p.fullName?.split(' ')[2] || '-'}</div>
                                         <div className="text-gray-600">{p.aadharNo || '-'}</div>
                                     </div>
                                 ))
                             ) : (
                                 <div className="p-4 text-center text-gray-500 font-medium">
                                     No records found
                                 </div>
                             )}
                         </div>
                    </div>
                </div>
            )}
            {/* New Person Entry Modal */}
            {isNewPersonModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] font-poppins text-sm px-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl overflow-hidden">
                        <div className="bg-[#267c77] text-white px-6 py-3 font-bold text-lg flex justify-between items-center">
                            <span>व्यक्तीची माहिती (नवीन नोंदणी)</span>
                            <button onClick={() => setIsNewPersonModalOpen(false)}><FiX size={24}/></button>
                        </div>
                        <div className="p-8 space-y-6 bg-white">
                            
                            <div className="flex items-center gap-4">
                                <label className="w-40 text-gray-700 font-medium text-right">शीर्षक</label>
                                <select 
                                    value={newPerson.title}
                                    onChange={(e) => setNewPerson({...newPerson, title: e.target.value})}
                                    className="border border-[#267c77]/30 px-3 py-2 rounded w-40 focus:ring-2 focus:ring-[#267c77] outline-none"
                                >
                                    <option>Mr.</option>
                                    <option>Mrs.</option>
                                    <option>Miss.</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-40 text-gray-700 font-medium text-right">पहिले नाव</label>
                                <input 
                                    value={newPerson.firstName}
                                    onChange={(e) => setNewPerson({...newPerson, firstName: e.target.value})}
                                    placeholder="पहिले नाव" 
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 focus:ring-2 focus:ring-[#267c77] outline-none" 
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-40 text-gray-700 font-medium text-right">वडिलांचे/पतीचे नाव</label>
                                <input 
                                    value={newPerson.middleName}
                                    onChange={(e) => setNewPerson({...newPerson, middleName: e.target.value})}
                                    placeholder="वडिलांचे/पतीचे नाव" 
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 focus:ring-2 focus:ring-[#267c77] outline-none" 
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-40 text-gray-700 font-medium text-right">आडनाव</label>
                                <input 
                                    value={newPerson.lastName}
                                    onChange={(e) => setNewPerson({...newPerson, lastName: e.target.value})}
                                    placeholder="आडनाव" 
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 focus:ring-2 focus:ring-[#267c77] outline-none" 
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-40 text-gray-700 font-medium text-right">भ्रमणध्वनी क्र</label>
                                <input 
                                    value={newPerson.mobileNo}
                                    onChange={(e) => setNewPerson({...newPerson, mobileNo: e.target.value})}
                                    placeholder="भ्रमणध्वनी क्र" 
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 focus:ring-2 focus:ring-[#267c77] outline-none" 
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-40 text-gray-700 font-medium text-right">आधार क्रमांक</label>
                                <input 
                                    value={newPerson.aadharNo}
                                    onChange={(e) => setNewPerson({...newPerson, aadharNo: e.target.value})}
                                    placeholder="आधार क्रमांक" 
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 focus:ring-2 focus:ring-[#267c77] outline-none" 
                                />
                            </div>

                             <div className="flex items-center gap-4">
                                <label className="w-40 text-gray-700 font-medium text-right">गावाचे नाव</label>
                                <input 
                                    value={newPerson.villageName}
                                    onChange={(e) => setNewPerson({...newPerson, villageName: e.target.value})}
                                    placeholder="गावाचे नाव" 
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 focus:ring-2 focus:ring-[#267c77] outline-none" 
                                />
                            </div>

                             <div className="flex justify-center gap-4 mt-8 pt-4 border-t border-gray-100">
                                <button 
                                    onClick={handleSaveNewPerson}
                                    className="bg-[#1e6b66] text-white px-8 py-2 rounded-lg font-bold text-lg hover:bg-[#155a55] shadow-md transition-colors"
                                >
                                    साठविणे
                                </button>
                                <button 
                                    onClick={() => setIsNewPersonModalOpen(false)}
                                    className="bg-red-500 text-white px-8 py-2 rounded-lg font-bold text-lg hover:bg-red-600 shadow-md transition-colors"
                                >
                                    रद्द करणे
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Print Namuna 7 */}
            {printMode === 'namuna7' && selectedDeposit && (
                <Namuna7Receipt 
                    transaction={selectedDeposit} 
                    onPrintComplete={() => {
                        setPrintMode(null);
                        setSelectedDeposit(null);
                    }} 
                />
            )}
            {/* Return List View */}
            {view === 'returnList' && selectedDepositForReturn && (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="bg-[#267c77] text-white px-6 py-4 flex justify-between items-center">
                        <h2 className="text-xl font-bold">अनामत माहिती</h2>
                        <button 
                            onClick={() => {
                                setView('list');
                                setSelectedDepositForReturn(null);
                            }}
                            className="bg-[#1e6b66] hover:bg-[#155a55] px-4 py-2 rounded text-sm font-bold"
                        >
                            मागे
                        </button>
                    </div>
                    
                    {/* Deposit Brief Info */}
                    <div className="p-6 border-b border-gray-200 bg-[#267c77]/10">
                        <div className="grid grid-cols-2 gap-8">
                            <div>
                                <p className="text-gray-600 font-bold">अनामत नं. <span className="text-black">{selectedDepositForReturn.depositNo}</span></p>
                                <p className="text-gray-600 font-bold mt-2">व्यक्तीचे नाव <span className="text-black">{selectedDepositForReturn.personName}</span></p>
                            </div>
                            <div className="text-right">
                                <p className="text-gray-600 font-bold">अनामत रक्कम <span className="text-black">{selectedDepositForReturn.depositAmount}</span></p>
                                <p className="text-gray-600 font-bold mt-2">शिल्लक रक्कम <span className="text-black">{(parseFloat(selectedDepositForReturn.depositAmount) - returns.reduce((sum, r) => sum + parseFloat(r.returnAmount || 0), 0)).toFixed(2)}</span></p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#267c77] text-white px-6 py-2 flex justify-between items-center mt-1">
                        <h3 className="text-lg font-bold">अनामत परत माहिती</h3>
                        <button 
                            onClick={() => setView('newReturn')}
                            className="bg-[#1e6b66] hover:bg-[#155a55] px-6 py-2 rounded font-bold shadow-md"
                        >
                            नविन
                        </button>
                    </div>

                    {/* Search Returns */}
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                        <div className="flex gap-2">
                             <input 
                                type="text" 
                                placeholder="शोधणे" 
                                className="border border-[#267c77]/30 rounded px-4 py-2 flex-grow focus:outline-none focus:ring-2 focus:ring-[#267c77]"
                            />
                            <button className="bg-[#267c77] text-white px-6 py-2 rounded font-bold hover:bg-[#1e6b66]">
                                शोधणे
                            </button>
                        </div>
                    </div>

                    {/* Returns Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-[#267c77] text-white text-sm">
                                <tr>
                                    <th className="p-3 border border-[#1e6b66] text-center">अनामत परत क्र.</th>
                                    <th className="p-3 border border-[#1e6b66] text-center">दिनांक</th>
                                    <th className="p-3 border border-[#1e6b66] text-center">रक्कम</th>
                                    <th className="p-3 border border-[#1e6b66] text-center">पर्याय</th>
                                </tr>
                            </thead>
                            <tbody>
                                {returns.length > 0 ? (
                                    returns.map((ret, idx) => (
                                        <tr key={ret.id} className="hover:bg-[#267c77]/10 border-b border-[#267c77]/30 font-medium">
                                            <td className="p-3 border border-[#267c77]/30 text-center">{idx + 1}</td>
                                            <td className="p-3 border border-[#267c77]/30 text-center">{new Date(ret.date).toLocaleDateString('en-GB')}</td>
                                            <td className="p-3 border border-[#267c77]/30 text-center">{ret.returnAmount}</td>
                                            <td className="p-3 border border-[#267c77]/30 text-center">
                                                <div className="flex justify-center gap-2">
                                                    <button 
                                                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded text-sm font-bold"
                                                        onClick={() => {
                                                            if(window.confirm('Delete this return?')) {
                                                                remove(ref(database, `Grampanchayat/Panipuravtha/Transactions/DepositReturns/${ret.id}`));
                                                            }
                                                        }}
                                                    >
                                                        खोडणे
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            setSelectedReturn({...ret, personName: selectedDepositForReturn.personName, totalAmount: ret.returnAmount});
                                                            setPrintMode('namuna12');
                                                        }}
                                                        className="bg-[#267c77] hover:bg-[#1e6b66] text-white px-4 py-1.5 rounded text-sm font-bold"
                                                    >
                                                        नमुना नं. 12
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            setSelectedDeposit(selectedDepositForReturn);
                                                            setPrintMode('namuna17');
                                                        }}
                                                        className="bg-[#1e6b66] hover:bg-[#155a55] text-white px-4 py-1.5 rounded text-sm font-bold"
                                                    >
                                                        रिपोर्ट
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center p-8 text-gray-500">No returns found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* New Return Form */}
            {view === 'newReturn' && selectedDepositForReturn && (
                <div className="bg-white rounded-lg shadow-md overflow-hidden font-poppins text-sm border border-[#267c77]/30">
                    <div className="bg-[#267c77] text-white px-6 py-4 flex justify-between items-center">
                        <h2 className="text-xl font-bold">अनामत परत माहिती</h2>
                        <button 
                             onClick={() => setView('returnList')}
                             className="bg-[#1e6b66] hover:bg-[#155a55] px-4 py-2 rounded text-sm font-bold"
                        >
                            मागे
                        </button>
                    </div>

                    {/* Brief Info */}
                    <div className="p-6 border-b border-gray-200 bg-[#267c77]/10">
                        <div className="grid grid-cols-2 gap-8">
                            <div>
                                <p className="text-gray-600 font-bold">अनामत नं. <span className="text-black">{selectedDepositForReturn.depositNo}</span></p>
                                <p className="text-gray-600 font-bold mt-2">व्यक्तीचे नाव <span className="text-black">{selectedDepositForReturn.personName}</span></p>
                            </div>
                           <div className="text-right">
                                <p className="text-gray-600 font-bold">अनामत रक्कम <span className="text-black">{selectedDepositForReturn.depositAmount}</span></p>
                                <p className="text-gray-600 font-bold mt-2">शिल्लक रक्कम <span className="text-black">{(parseFloat(selectedDepositForReturn.depositAmount) - returns.reduce((sum, r) => sum + parseFloat(r.returnAmount || 0), 0)).toFixed(2)}</span></p>
                            </div>
                        </div>
                    </div>

                    <div className="p-8 space-y-6">
                        {/* Date */}
                         <div className="flex items-center gap-4">
                            <label className="w-40 text-gray-700 font-medium text-right">दिनांक:</label>
                            <input 
                                type="date"
                                value={newReturn.date}
                                onChange={(e) => setNewReturn({...newReturn, date: e.target.value})}
                                className="border border-[#267c77]/30 px-3 py-2 rounded w-64 focus:ring-2 focus:ring-[#267c77] outline-none" 
                            />
                        </div>

                        {/* Return Amount */}
                        <div className="flex items-center gap-4">
                            <label className="w-40 text-gray-700 font-medium text-right">परत रक्कम:</label>
                            <input 
                                type="number"
                                value={newReturn.returnAmount}
                                onChange={(e) => setNewReturn({...newReturn, returnAmount: e.target.value})}
                                placeholder="परत करण्याची रक्कम भरा." 
                                className="border border-[#267c77]/30 px-3 py-2 rounded w-64 focus:ring-2 focus:ring-[#267c77] outline-none" 
                            />
                        </div>

                         {/* Payment Mode */}
                         <div className="flex items-center gap-4">
                            <label className="w-40 text-gray-700 font-medium text-right">व्यवहार प्रकार</label>
                            <div className="flex items-center gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input 
                                        type="radio" 
                                        name="returnPaymentMode" 
                                        checked={newReturn.paymentMode === 'cash'}
                                        onChange={() => setNewReturn({...newReturn, paymentMode: 'cash'})}
                                        className="w-5 h-5 text-[#267c77]"
                                    />
                                    <span>रोख</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input 
                                        type="radio" 
                                        name="returnPaymentMode" 
                                        checked={newReturn.paymentMode === 'transfer'}
                                        onChange={() => setNewReturn({...newReturn, paymentMode: 'transfer'})}
                                        className="w-5 h-5 text-[#267c77]"
                                    />
                                    <span>वर्ग / ट्रान्सफर</span>
                                </label>
                            </div>
                        </div>

                         {/* Transaction Details Section */}
                        <div className="bg-[#267c77] text-white px-4 py-2 rounded -mx-8 mt-6 mb-6">
                            <h3 className="font-bold">व्यवहार तपशील</h3>
                        </div>

                         {/* Voucher & Date */}
                        <div className="flex gap-8">
                             <div className="flex-1 flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium text-right">व्हौचर नं. :</label>
                                <input 
                                    value={newReturn.voucherNo}
                                    onChange={(e) => setNewReturn({...newReturn, voucherNo: e.target.value})}
                                    placeholder="भरा." 
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 outline-none" 
                                />
                            </div>
                             <div className="flex-1 flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium text-right">दिनांक :</label>
                                <input 
                                    type="date"
                                    value={newReturn.voucherDate}
                                    onChange={(e) => setNewReturn({...newReturn, voucherDate: e.target.value})}
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 outline-none bg-gray-100" 
                                />
                            </div>
                        </div>

                        {/* Person Name (Read Only) */}
                        <div className="flex items-center gap-4">
                             <label className="w-32 text-gray-700 font-medium text-right">व्यक्तीचे नाव :</label>
                             <input 
                                value={selectedDepositForReturn.personName} 
                                readOnly 
                                className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 bg-gray-200 cursor-not-allowed outline-none" 
                            />
                        </div>

                        {/* Resolution & Date */}
                        <div className="flex gap-8">
                             <div className="flex-1 flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium text-right">ठराव नं. :</label>
                                <input 
                                    value={newReturn.resolutionNo}
                                    onChange={(e) => setNewReturn({...newReturn, resolutionNo: e.target.value})}
                                    placeholder="ठराव नं. भरा." 
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 outline-none" 
                                />
                            </div>
                             <div className="flex-1 flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium text-right">ठराव दिनांक:</label>
                                <input 
                                    type="date"
                                    value={newReturn.resolutionDate}
                                    onChange={(e) => setNewReturn({...newReturn, resolutionDate: e.target.value})}
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 outline-none" 
                                />
                            </div>
                        </div>

                        {/* Ledger & Allocation Amount */}
                         <div className="flex gap-8">
                             <div className="flex-1 flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium text-right">खतावणी :</label>
                                 <select 
                                    value={newReturn.ledgerId}
                                    onChange={(e) => setNewReturn({...newReturn, ledgerId: e.target.value})}
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 outline-none w-full bg-[#267c77]/10"
                                >
                                    <option value="">-</option>
                                    {ledgers.map(l => (
                                        <option key={l.id} value={l.id}>{l.groupName}</option>
                                    ))}
                                </select>
                                <button className="bg-[#267c77] text-white px-3 py-2 rounded hover:bg-[#1e6b66]">...</button>
                            </div>
                             <div className="flex-1 flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium text-right">वाटणीची रक्कम :</label>
                                <input 
                                    value={"0"} 
                                    readOnly
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 outline-none bg-gray-200" 
                                />
                            </div>
                        </div>

                         {/* Account & Balance */}
                         <div className="flex gap-8">
                             <div className="flex-1 flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium text-right">खाते :</label>
                                 <input 
                                    value={newReturn.accountId}
                                    onChange={(e) => setNewReturn({...newReturn, accountId: e.target.value})}
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 outline-none bg-gray-200"
                                />
                                <button className="bg-[#267c77] text-white px-3 py-2 rounded hover:bg-[#1e6b66]">...</button>
                            </div>
                             <div className="flex-1 flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium text-right">खाते शिल्लक :</label>
                                <input 
                                    value={newReturn.accountBalance}
                                    readOnly
                                    className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 outline-none bg-gray-200" 
                                />
                            </div>
                        </div>
                        
                        {/* Narration */}
                        <div className="flex items-center gap-4">
                             <label className="w-32 text-gray-700 font-medium text-right">व्यवहार रक्कम :</label>
                             <input 
                                value={newReturn.returnNarration}
                                onChange={(e) => setNewReturn({...newReturn, returnNarration: e.target.value})}
                                placeholder="रक्कम भरा." 
                                className="border border-[#267c77]/30 px-3 py-2 rounded flex-1 outline-none bg-gray-200" 
                            />
                        </div>

                         {/* Action Buttons */}
                        <div className="flex justify-center gap-4 mt-8 pt-4">
                             <button 
                                onClick={handleSaveReturn}
                                className="bg-[#1e6b66] text-white px-8 py-2 rounded font-bold hover:bg-[#155a55] shadow-md"
                            >
                                साठविणे
                            </button>
                             <button 
                                className="bg-red-500 text-white px-8 py-2 rounded font-bold hover:bg-red-600 shadow-md"
                            >
                                रद्द करणे
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Print Namuna 17 Report */}
            {printMode === 'namuna17' && selectedDeposit && (
                <Namuna17Report 
                    loans={[selectedDeposit]} 
                    reportType="deposit"
                    onPrintComplete={() => {
                        setPrintMode(null);
                        setSelectedDeposit(null);
                    }} 
                />
            )}
            {/* Print Namuna 12 Receipt */}
            {printMode === 'namuna12' && selectedReturn && (
                <Namuna12Receipt 
                    transaction={selectedReturn} 
                    onPrintComplete={() => {
                        setPrintMode(null);
                        setSelectedReturn(null);
                    }} 
                />
            )}
        </div>
    );
};

export default DepositManagement;
